import {
	AfterViewInit,
	Component,
	ContentChildren,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	Output,
	QueryList,
	Renderer2,
	ViewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatTab, MatTabContent, MatTabGroup } from '@angular/material/tabs';
import { Subscription } from 'rxjs';
import { QueryStringTabGroupDirective } from '../../directives/query-string-tab-group.directive';
import { TabAutomationIdDirective } from '../../directives/tab-automation-id.directive';
import { SubNavTabDirective } from './sub-nav-tab.directive';

/**
 * Shared sub-header + optional sub-tab bar for company-detail embeds (Users, Properties, …).
 * Screens declare tabs with `<ng-template appSubNavTab>`. With no tabs, project body
 * content under the title (title-only section).
 */
@Component({
	selector: 'app-sub-nav',
	templateUrl: './sub-nav.component.html',
	styleUrl: './sub-nav.component.scss',
	imports: [
		MatTabGroup,
		MatTab,
		MatTabContent,
		NgTemplateOutlet,
		QueryStringTabGroupDirective,
		TabAutomationIdDirective,
	],
})
export class SubNavComponent implements AfterViewInit, OnDestroy {
	/** Optional section title above the sub-tabs (e.g. "Users", "Properties"). */
	@Input() title?: string;

	/**
	 * Optional parent crumb before the title (e.g. "Properties" in
	 * `Properties → [Property Name]`). When set, rendered as a clickable link.
	 */
	@Input() parentTitle?: string;

	/**
	 * Query string key for this tab group. Required when tabs are declared so it
	 * never collides with a parent page's `tabIndex`. Unused in title-only mode.
	 */
	@Input() queryParamKey?: string;

	/** Opt in to URL updates; default true for company-detail embeds. */
	@Input() syncUrl = true;

	@Output() selectedIndexChange = new EventEmitter<number>();

	/** Emitted when the parent crumb is clicked (e.g. Properties → [Name]). */
	@Output() parentTitleClick = new EventEmitter<void>();

	/**
	 * Direct content children only (`descendants: false`) so a nested sub-nav
	 * inside tab content does not leak tabs into the parent.
	 */
	@ContentChildren(SubNavTabDirective)
	tabs!: QueryList<SubNavTabDirective>;

	@ViewChild(MatTabGroup) private tabGroup?: MatTabGroup;

	private tabsChangeSubscription?: Subscription;

	constructor(
		private readonly el: ElementRef<HTMLElement>,
		private readonly renderer: Renderer2
	) {}

	get hasTabs(): boolean {
		return (this.tabs?.length ?? 0) > 0;
	}

	ngAfterViewInit(): void {
		if (!this.hasTabs) {
			return;
		}
		// Registered after TabAutomationIdDirective's ngAfterViewInit, so this
		// timeout runs second and replaces label-slug ids with automationId.
		this.scheduleAutomationIds();
		this.tabsChangeSubscription = this.tabGroup?._tabs.changes.subscribe(() => {
			this.scheduleAutomationIds();
		});
	}

	ngOnDestroy(): void {
		this.tabsChangeSubscription?.unsubscribe();
		this.tabsChangeSubscription = undefined;
	}

	private scheduleAutomationIds(): void {
		setTimeout(() => this.applyAutomationIds());
	}

	/** This group's header only — nested sub-nav headers are not descendants of it. */
	private applyAutomationIds(): void {
		const header = this.el.nativeElement.querySelector(
			'.sub-nav-tabs > .mat-mdc-tab-header'
		);
		if (!header) {
			return;
		}

		const headers = header.querySelectorAll('.mat-mdc-tab');
		this.tabs?.forEach((tab, index) => {
			const headerEl = headers[index];
			if (!tab.automationId || !headerEl) {
				return;
			}
			this.renderer.setAttribute(
				headerEl,
				'data-automation-id',
				tab.automationId
			);
		});
	}
}
